/**
 * ResultsGrid — keyword/semantic hits in an MUI X DataGrid.
 *
 * Each row is one search hit; clicking a row opens the document drawer. The
 * snippet column renders highlighted matches safely (no raw HTML injection).
 * The grid lives in a labelled region for screen-reader navigation.
 */
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { DataGrid, type GridColDef, type GridRowParams } from "@mui/x-data-grid";
import { useMemo, type ReactElement } from "react";
import { HighlightedSnippet } from "./HighlightedSnippet";
import type { SearchHit } from "@records-monitor/shared";

export interface ResultsGridProps {
  hits: readonly SearchHit[];
  loading: boolean;
  /** True once a query has run (controls empty-state copy). */
  hasQuery: boolean;
  onOpenDocument: (hit: SearchHit) => void;
}

function formatMeetingDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
}

export function ResultsGrid({
  hits,
  loading,
  hasQuery,
  onOpenDocument,
}: ResultsGridProps): ReactElement {
  const columns = useMemo<GridColDef<SearchHit>[]>(
    () => [
      {
        field: "documentTitle",
        headerName: "Document",
        flex: 1.4,
        minWidth: 180,
        valueGetter: (_value, row) => row.documentTitle ?? "Untitled document",
      },
      {
        field: "jurisdictionName",
        headerName: "Jurisdiction",
        flex: 1,
        minWidth: 120,
        valueGetter: (_value, row) => row.jurisdictionName ?? "—",
      },
      {
        field: "bodyName",
        headerName: "Body",
        flex: 1,
        minWidth: 120,
        valueGetter: (_value, row) => row.bodyName ?? "—",
      },
      {
        field: "meetingDate",
        headerName: "Meeting date",
        width: 130,
        valueGetter: (_value, row) => formatMeetingDate(row.meetingDate),
      },
      {
        field: "snippetWithHighlights",
        headerName: "Match",
        flex: 2,
        minWidth: 220,
        sortable: false,
        renderCell: (cellParams) => (
          <HighlightedSnippet snippet={cellParams.row.snippetWithHighlights} />
        ),
      },
    ],
    [],
  );

  return (
    <Box component="section" aria-label="Search results" sx={{ width: "100%", minHeight: 360 }}>
      {!hasQuery && !loading ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          Enter a query to search public-meeting records.
        </Typography>
      ) : (
        <DataGrid
          rows={hits as SearchHit[]}
          columns={columns}
          getRowId={(row) => row.chunkId}
          loading={loading}
          disableRowSelectionOnClick
          autoHeight
          onRowClick={(params: GridRowParams<SearchHit>) => onOpenDocument(params.row)}
          getRowHeight={() => "auto"}
          localeText={{
            noRowsLabel: hasQuery ? "No matching records." : "Enter a query to search.",
          }}
          sx={{
            "& .MuiDataGrid-row": { cursor: "pointer" },
            "& .MuiDataGrid-cell": { py: 1, alignItems: "flex-start" },
          }}
          aria-label="Search results table"
        />
      )}
    </Box>
  );
}
